import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import MapView from '@arcgis/core/views/MapView';
import { ComponentContainer } from 'golden-layout';
import config from '@arcgis/core/config';
import MapImageLayer from '@arcgis/core/layers/MapImageLayer';
import FindParameters from '@arcgis/core/rest/support/FindParameters';
import Graphic from '@arcgis/core/Graphic';
import Search from '@arcgis/core/widgets/Search';
import { ArxThematicWidget } from '@arxit/esri/widgets/ArxThematic';
import { Store } from '@ngrx/store';

import { GoldenLayoutContainerInjectionToken, WidgetHostDirective } from '../../../_directives/widget-host.directive';
import { IZoomEvent } from '../../../_models/models';
import { ConfigService } from '../../../_services/local/config.service';
import { RefreshMapService } from '../../../_services/http/refresh-map.service';
import { EsriHelpers } from '../../../_helpers/EsriHelpers';
import { IBaseObject } from '../../../_models/BaseObject';
import { MainStoreState } from 'src/app/_store/mainStoreState';

@Component({
  selector: 'app-widget-map-vec',
  templateUrl: './map-vec.component.html',
  styleUrls: ['./map-vec.component.scss'],
})
export class MapVecComponent extends WidgetHostDirective implements OnInit, AfterViewInit, OnDestroy {
  private readonly portalUrl = this.configService.Config.portalUrl;
  private readonly mapId = this.configService.Config.mapVecId;

  private onDestroy$ = new Subject<boolean>();
  private channel = new BroadcastChannel('map-vec');

  @Output() closed = new EventEmitter<{ event: Event; uuid: string }>();
  @Output() openedIneNew = new EventEmitter<{ event: Event; uuid: string }>();
  @Output() resized = new EventEmitter<number>();
  @ViewChild('mapViewNode', { static: true }) private mapViewEl!: ElementRef;

  private mapView!: MapView;

  constructor(
    @Inject(GoldenLayoutContainerInjectionToken) container: ComponentContainer,
    elRef: ElementRef,
    renderer: Renderer2,
    viewContainerRef: ViewContainerRef,
    store: Store<MainStoreState>,
    private configService: ConfigService,
    private refreshMapService: RefreshMapService
  ) {
    super(container, elRef.nativeElement, renderer, viewContainerRef, store);

    this.channel.addEventListener('message', async (evt: MessageEvent<IZoomEvent<IBaseObject>>) => {
      if (evt.data.type === 'zoom' && this.mapView) {
        await this.zoom(evt.data);
      }
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.calculateHeight(this.container.height);

    this.initializeMap().then(() => {
      const arxThematic = new ArxThematicWidget({
        view: this.mapView,
        shouldApplyGrayscaleWhenUnchecked: true,
        thematics: this.configService.Config.thematicsVEC,
        position: 'start',
        resizable: true,
        collapsible: true,
        open: false,
      });
      this.mapView.ui.add(arxThematic, 'top-left');
      const searchWidget = new Search({
        view: this.mapView,
        sources: this.configService.Config.search.sources,
        includeDefaultSources: false,
      });
      this.mapView.ui.add(searchWidget, { position: 'top-trailing' });

      if (this.config.item) {
        this.zoom(this.config.item);
      }
      this.loading = false;

      this.refreshMapService.refreshHubConnection();
      this.refreshMapService.refreshed$.subscribe(() => {
        EsriHelpers.refreshView(this.mapView);
      });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next(true);
    this.mapView.destroy();
    this.channel.close();
  }

  async initializeMap(): Promise<void> {
    try {
      config.portalUrl = this.portalUrl;

      this.mapView = await EsriHelpers.GenerateMapAsync(this.mapViewEl.nativeElement, this.mapId, undefined, { activateIdentify: true });
      EsriHelpers.listenToPopupFeaturesChange(this.mapView, this.configService.Config.thematicsVEC);
    } catch (error) {
      console.debug('EsriLoader: ', error);
    }
  }

  private async zoom(evt: IZoomEvent<IBaseObject>): Promise<void> {
    console.debug(evt);

    const params = new FindParameters({
      returnGeometry: true,
      outSpatialReference: this.mapView.spatialReference,
      searchText: evt.searchText,
      searchFields: [evt.searchField],
      layerIds: evt.subLayerIds,
    });

    const graphics: Graphic[] = [];

    for await (const layer of this.mapView.map.allLayers.toArray()) {
      if (layer instanceof MapImageLayer) {
        if ((evt.layerUrl && layer.url === evt.layerUrl) || (evt.layerId && layer.id === evt.layerId)) {
          let popupTemplate: any = null;
          layer.allSublayers.forEach((sl) => {
            if (evt.subLayerIds.includes(sl.id)) {
              popupTemplate = sl.popupTemplate;
            }
          });
          const res = await EsriHelpers.find(layer, params);

          graphics.push(
            ...res.map((r) => {
              r.feature.attributes.popupTitle = `Couche : ${layer.title || layer.id}`;
              r.feature.popupTemplate = popupTemplate;

              return r.feature;
            })
          );
        }
      }
    }

    if (graphics.length) {
      EsriHelpers.togglePopupTemplate(this.mapView, graphics);
    }
  }
}
